import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/rallies', label: 'Rallies' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/sponsors', label: 'Sponsors' },
    { path: '/mission', label: 'Mission' },
    { path: '/faq', label: 'FAQ' },
  ];

  return (
    <>
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass py-3' : 'bg-transparent py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img 
              src="https://lirp.cdn-website.com/6920c96d/dms3rep/multi/opt/Revline+Rally-1920w.png" 
              alt="Revline Rally" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`font-['Oswald'] text-sm tracking-wider uppercase transition-colors ${
                  location.pathname === link.path
                    ? 'text-[#E8651A]'
                    : 'text-[#F2EDE6] hover:text-[#E8651A]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {user ? (
              <Link
                to="/account"
                data-testid="nav-account"
                className="flex items-center gap-2 font-['Oswald'] text-sm tracking-wider uppercase text-[#F2EDE6] hover:text-[#E8651A]"
              >
                <User size={16} />
                {user.name?.split(' ')[0] || 'Account'}
              </Link>
            ) : (
              <Link
                to="/login"
                data-testid="nav-login"
                className="font-['Oswald'] text-sm tracking-wider uppercase text-[#F2EDE6] hover:text-[#E8651A]"
              >
                Log In
              </Link>
            )}
            <Link
              to="/rallies#signup"
              data-testid="nav-cta"
              className="btn-primary text-sm"
            >
              Join a Rally
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-[#F2EDE6] p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-[#0A0A0A] pt-20"
          >
            <div className="flex flex-col items-center gap-6 p-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  className={`font-['Oswald'] text-2xl tracking-wider uppercase ${
                    location.pathname === link.path
                      ? 'text-[#E8651A]'
                      : 'text-[#F2EDE6]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {user ? (
                <Link
                  to="/account"
                  data-testid="mobile-nav-account"
                  className="font-['Oswald'] text-2xl tracking-wider uppercase text-[#F2EDE6] flex items-center gap-2"
                >
                  <User size={20} />
                  Account
                </Link>
              ) : (
                <Link
                  to="/login"
                  data-testid="mobile-nav-login"
                  className="font-['Oswald'] text-2xl tracking-wider uppercase text-[#F2EDE6]"
                >
                  Log In
                </Link>
              )}
              <Link
                to="/rallies#signup"
                data-testid="mobile-nav-cta"
                className="btn-primary text-lg mt-4"
              >
                Join a Rally
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
